import projectConfig, { PROJECT } from '~/projects/config';

/**
 * Brand-specific colors for the PDF report.
 *
 * react-pdf cannot read CSS variables, so these are mirrored from each
 * project's SCSS palette as literals. Only tokens that actually differ between
 * projects live here — semantic tokens (neutral / error / warning) are
 * identical across brands and stay in `reportTheme`.
 */
export type ReportBrandColors = {
  primary_20: string;
  primary_30: string;
  primary_35: string;
  primary_40: string;
  primary_50: string;
  primary_60: string;
  primary_80: string;
  primary_90: string;
  secondary_80: string;
  green_50: string;
  green_80: string;
  gradient_60_start: string;
  gradient_60_end: string;
  source_index_gradient_start: string;
  source_index_gradient_end: string;
  sparkle_gradient_start: string;
  sparkle_gradient_end: string;
};

export type ReportBranding = {
  /** Lowercase wordmark printed next to the sparkle icon in the page header. */
  wordmark: string;
  /** Display name used in prose, the footer and PDF metadata. */
  brandName: string;
  /** Engine name shown on the scan settings page. */
  engineLabel: string;
  footerUrl: string;
  footerUrlLabel: string;
  /**
   * Deep link to the project's citation tool, or `null` when the project does
   * not ship one — Sidekicker has no Citation Machine, so its CTA is hidden
   * rather than pointing at another product.
   */
  citationToolUrl: string | null;
  colors: ReportBrandColors;
};

const JUSTDONE_REPORT_BRANDING: ReportBranding = {
  wordmark: 'justdone',
  brandName: 'JustDone',
  engineLabel: 'JustDone Originality v1',
  footerUrl: 'https://justdone.com/plagiarism-checker',
  footerUrlLabel: 'justdone.com/plagiarism-checker',
  citationToolUrl: 'https://app.justdone.ai/tools/citation_machine',
  colors: {
    primary_20: '#00164F',
    primary_30: '#13307E',
    primary_35: '#2F4990',
    primary_40: '#3B5AAE',
    primary_50: '#708DE4',
    primary_60: '#A5B8F4',
    primary_80: '#DBE1FF',
    primary_90: '#EBEEFF',
    secondary_80: '#EFEAF6',
    green_50: '#00B39B',
    green_80: '#D7F4F3',
    gradient_60_start: '#D9F3F0',
    gradient_60_end: '#E1EAFE',
    source_index_gradient_start: '#4A72DB',
    source_index_gradient_end: '#7DC9D0',
    sparkle_gradient_start: '#00B39B',
    sparkle_gradient_end: '#3B71F7',
  },
};

const SIDEKICKER_REPORT_BRANDING: ReportBranding = {
  wordmark: 'sidekicker',
  brandName: 'Sidekicker',
  engineLabel: 'Sidekicker Originality v1',
  footerUrl: 'https://sidekicker.ai/plagiarism-checker',
  footerUrlLabel: 'sidekicker.ai/plagiarism-checker',
  // Sidekicker does not ship Citation Machine (see projects/sidekicker/constants/tools).
  citationToolUrl: null,
  colors: {
    primary_20: '#003F35',
    primary_30: '#004D40',
    primary_35: '#00594A',
    primary_40: '#006A59',
    primary_50: '#4D978B',
    primary_60: '#8CBCB4',
    primary_80: '#CCE1DE',
    primary_90: '#E5F0EE',
    secondary_80: '#E6F8F5',
    green_50: '#006A59',
    green_80: '#CCE1DE',
    gradient_60_start: '#E5F0EE',
    gradient_60_end: '#CCE1DE',
    source_index_gradient_start: '#006A59',
    source_index_gradient_end: '#4D978B',
    // The Sidekicker logo is a single flat color, so the header icon uses a
    // subtle tonal gradient from its own primaries instead of JustDone's
    // teal-to-blue one.
    sparkle_gradient_start: '#006A59',
    sparkle_gradient_end: '#4D978B',
  },
};

const REPORT_BRANDING_BY_PROJECT: Record<PROJECT, ReportBranding> = {
  [PROJECT.JUSTDONE]: JUSTDONE_REPORT_BRANDING,
  [PROJECT.SIDEKICKER]: SIDEKICKER_REPORT_BRANDING,
};

export const getReportBranding = (project: PROJECT): ReportBranding =>
  REPORT_BRANDING_BY_PROJECT[project];

/**
 * Project whose branding the report is rendered with — the build's own project
 * (`process.env.PROJECT`). Resolved once at module load: the report is rendered
 * inside a single build, so the brand cannot change at runtime.
 */
export const reportBrandProject: PROJECT = projectConfig.project;

export const reportBranding: ReportBranding =
  getReportBranding(reportBrandProject);
